/**
 * Keeps the search `location[]` labels and `placeIds[]` aligned by index.
 *
 * A label that carries a Place ID is resolved server-side with (cached) Place
 * Details; a label without one falls back to Geocoding. Losing the pairing
 * therefore silently converts a cheap, cacheable lookup into a paid Geocoding
 * call, so every add/remove must update both arrays in one step.
 *
 * `placeIds` uses the empty string as "no Place ID for this label" — needed for
 * state labels (Bayern, …), which are filtered by state code and never resolved
 * through Places, and for legacy URLs that predate Place ID persistence.
 */

export type LocationSelection = {
  location: string[];
  /** Same length as `location`; `""` means "no Place ID known for this label". */
  placeIds: string[];
};

/**
 * Returns a `placeIds` array that is exactly as long as `location`, padding with
 * empty strings and dropping trailing entries that no longer have a label.
 */
export function alignPlaceIds(
  location: string[],
  placeIds: string[] | undefined,
): string[] {
  const aligned = Array.from(
    { length: location.length },
    (_, index) => placeIds?.[index] ?? "",
  );
  return aligned;
}

/**
 * Adds a label/Place ID pair, or backfills the Place ID when the label is
 * already selected without one. Never produces duplicate labels.
 */
export function addLocationSelection(
  location: string[] | undefined,
  placeIds: string[] | undefined,
  label: string,
  placeId: string,
): LocationSelection {
  const currentLocations = location ?? [];
  const aligned = alignPlaceIds(currentLocations, placeIds);
  const existingIndex = currentLocations.indexOf(label);

  if (existingIndex === -1) {
    return {
      location: [...currentLocations, label],
      placeIds: [...aligned, placeId],
    };
  }

  if (!aligned[existingIndex] && placeId) {
    const nextPlaceIds = [...aligned];
    nextPlaceIds[existingIndex] = placeId;
    return { location: currentLocations, placeIds: nextPlaceIds };
  }

  return { location: currentLocations, placeIds: aligned };
}

/** Removes the label at `index` together with its Place ID. */
export function removeLocationSelectionAt(
  location: string[] | undefined,
  placeIds: string[] | undefined,
  index: number,
): LocationSelection {
  const currentLocations = location ?? [];
  const aligned = alignPlaceIds(currentLocations, placeIds);
  return {
    location: currentLocations.filter((_, i) => i !== index),
    placeIds: aligned.filter((_, i) => i !== index),
  };
}

/**
 * Drops an all-empty `placeIds` array so it never appears in the URL, since it
 * carries no information the labels don't already imply.
 */
export function placeIdsForUrl(placeIds: string[]): string[] | undefined {
  return placeIds.some((id) => id.length > 0) ? placeIds : undefined;
}
